/**
 * Bundle Builder (BYOB) — Terra Clays — PROGRESSIVE model
 *
 * Custom element <bundle-builder>. The customer fills a box with any number of kits
 * (atoms). The discount scales with the total number of kits — "the more you add, the
 * more you save" — via a ladder (count -> %). Quick-start presets pre-fill the box with
 * a curated trio's kits (then the customer can swap/add). Min 3 to add.
 *
 * Each kit is added as its OWN cart line; a cart-wide Product Discount Function applies
 * the tier % to all Single-Kit lines at checkout. The savings shown here are computed
 * client-side from the same ladder so the preview matches the real cart.
 */
(function () {
  'use strict';
  if (window.customElements && customElements.get('bundle-builder')) return;

  var SEL = {
    config: '[data-bb-config]',
    grid: '[data-bb-grid]',
    kit: '[data-bb-kit]',
    add: '[data-bb-add]',
    inc: '[data-bb-inc]',
    dec: '[data-bb-dec]',
    stepper: '[data-bb-stepper]',
    badge: '[data-bb-kit-badge]',
    preset: '[data-bb-preset]',
    tab: '[data-bb-tab]',
    slots: '[data-bb-slots]',
    counter: '[data-bb-counter]',
    tier: '[data-bb-tier]',
    progress: '[data-bb-progress]',
    seg: '[data-bb-seg]',
    total: '[data-bb-total]',
    was: '[data-bb-was]',
    savingsBox: '[data-bb-savings-box]',
    savingsAmt: '[data-bb-savings-amt]',
    hint: '[data-bb-hint]',
    mode: '[data-bb-mode]',
    modeLabel: '[data-bb-mode-label]',
    plans: '[data-bb-plans]',
    sellingPlan: '[data-bb-selling-plan]',
    form: '[data-bb-form]',
    items: '[data-bb-items]',
    submit: '[data-bb-submit]'
  };

  function money(cents, cfg) {
    cents = Math.round(cents) || 0;
    var format =
      (cfg.useCurrencyCode ? cfg.currencyFormatWithCode : cfg.currencyFormat) ||
      (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var rx = /\{\{\s*(\w+)\s*\}\}/;
    function build(precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var n = (cents / 100).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(rx);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = build(0); break;
      case 'amount_with_comma_separator': value = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = build(0, '.', ','); break;
      case 'amount_with_space_separator': value = build(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = build(0, ' ', ''); break;
      default: value = build(2);
    }
    return format.replace(rx, value);
  }

  function tmpl(str, map) {
    return String(str || '').replace(/%(\w+)%/g, function (_, k) {
      return map[k] != null ? map[k] : '';
    });
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      var cfgEl = this.querySelector(SEL.config);
      this.cfg = { minKits: 3, ladder: [], strings: {} };
      if (cfgEl) { try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) {} }
      this.cfg.strings = this.cfg.strings || {};
      // ladder: [{min, pct}] sorted ascending by min
      this.ladder = (this.cfg.ladder || []).slice().sort(function (a, b) { return a.min - b.min; });
      this.minKits = this.cfg.minKits || 3;
      this.presetsMax = this.cfg.presetsMax || 6; // cap visible quick-start presets per tab

      this.slotsEl = this.querySelector(SEL.slots);
      this.counterEl = this.querySelector(SEL.counter);
      this.tierEl = this.querySelector(SEL.tier);
      this.progressEl = this.querySelector(SEL.progress);
      this.segs = Array.prototype.slice.call(this.querySelectorAll(SEL.seg));
      this.tabs = Array.prototype.slice.call(this.querySelectorAll(SEL.tab));
      this.totalEl = this.querySelector(SEL.total);
      this.wasEl = this.querySelector(SEL.was);
      this.savingsBox = this.querySelector(SEL.savingsBox);
      this.savingsAmt = this.querySelector(SEL.savingsAmt);
      this.hintEl = this.querySelector(SEL.hint);
      this.form = this.querySelector(SEL.form);
      this.itemsEl = this.querySelector(SEL.items);
      this.submitBtn = this.querySelector(SEL.submit);
      this.plansEl = this.querySelector(SEL.plans);
      this.sellingPlanSelect = this.querySelector(SEL.sellingPlan);

      // mobile sticky bar mirrors of the panel total / count / hint / submit
      this.mCount = this.querySelector('[data-bb-m-count]');
      this.mTotal = this.querySelector('[data-bb-m-total]');
      this.mWas = this.querySelector('[data-bb-m-was]');
      this.mHint = this.querySelector('[data-bb-m-hint]');
      this.mSubmit = this.querySelector('[data-bb-m-submit]');

      this._analyticsStarted = false;
      this.selection = {}; // variantId -> qty
      this.order = [];      // unique variantIds in add order
      this.mode = 'onetime';

      this.bindEvents();
      var activeTab = this.querySelector(SEL.tab + '.is-active');
      if (activeTab) this.applyTab(activeTab);
      this.render();
    }

    bindEvents() {
      var self = this;

      // ----- Mobile bottom-sheet: the "your box" panel opens from the bar handle -----
      function setSheet(open) {
        self.classList.toggle('bb--sheet-open', open);
        var opener = self.querySelector('[data-bb-sheet-open]');
        if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      this._setSheet = setSheet;
      this.querySelectorAll('[data-bb-sheet-open]').forEach(function (el) {
        el.addEventListener('click', function () { setSheet(true); });
      });
      this.querySelectorAll('[data-bb-sheet-close]').forEach(function (el) {
        el.addEventListener('click', function () { setSheet(false); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') setSheet(false);
      });

      var grid = this.querySelector(SEL.grid);
      if (grid) {
        grid.addEventListener('click', function (e) {
          var kit = e.target.closest(SEL.kit);
          if (!kit) return;
          if (kit.getAttribute('data-unavailable') === 'true') return; // sold out — not clickable
          var vid = kit.getAttribute('data-variant-id');
          if (!vid) return;
          // Clicking the '−' stepper removes one; clicking anywhere else on the card
          // (image, title, '+ Add', or '+') adds one.
          if (e.target.closest(SEL.dec)) self.changeQty(vid, -1);
          else self.changeQty(vid, 1);
        });
      }

      // Quick-start presets — ADD the preset's kits (stacking presets climbs tiers)
      this.querySelectorAll(SEL.preset).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var ids = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
          ids.forEach(function (vid) { self.changeQty(vid, 1, true); });
          self.render();
          self.scrollPanel();
        });
      });

      // Range tabs — filter the kit grid + presets by range (empty tab = show all)
      this.tabs.forEach(function (btn) {
        btn.addEventListener('click', function () { self.applyTab(btn); });
      });

      this.querySelectorAll(SEL.mode).forEach(function (radio) {
        radio.addEventListener('change', function () {
          self.mode = radio.value === 'subscribe' ? 'subscribe' : 'onetime';
          self.querySelectorAll(SEL.modeLabel).forEach(function (l) {
            l.classList.toggle('is-active', l.contains(radio) && radio.checked);
          });
          if (self.plansEl) self.plansEl.hidden = self.mode !== 'subscribe';
          self.analyticsEvent('bundle_selection_changed', { action: 'mode' });
        });
      });

      if (this.form) {
        this.form.addEventListener('submit', function (e) { e.preventDefault(); setSheet(false); self.addToCart(); });
      }

      if (this.mSubmit) {
        this.mSubmit.addEventListener('click', function () { self.addToCart(); });
      }
    }

    scrollPanel() {
      var panel = this.querySelector('[data-bb-panel]');
      if (panel && window.matchMedia('(max-width: 749px)').matches) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /** activate a range tab: filter kits by range, and show a curated (capped) set of presets */
    applyTab(btn) {
      var range = btn.getAttribute('data-bb-tab') || '';
      this.tabs.forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      // PDP-style theming: recolour the whole builder to the selected range's
      // background (the "All" tab carries the neutral default). CSS transitions it.
      var bg = btn.getAttribute('data-bb-tab-bg');
      if (bg) this.style.setProperty('--bb-bg', bg);
      // kits: show every kit in the active range (no cap)
      this.querySelectorAll(SEL.kit).forEach(function (el) {
        var r = el.getAttribute('data-bb-range') || '';
        el.classList.toggle('is-hidden', range !== '' && r !== range);
      });
      // presets: a range tab shows that range's complete set; the "All" tab shows bestsellers across ranges
      var isAll = range === '';
      var presets = this.querySelectorAll(SEL.preset);
      var anyBest = false;
      if (isAll) {
        presets.forEach(function (el) { if (el.getAttribute('data-bb-bestseller') === 'true') anyBest = true; });
      }
      var max = this.presetsMax || 999;
      var shown = 0;
      presets.forEach(function (el) {
        var r = el.getAttribute('data-bb-range') || '';
        var best = el.getAttribute('data-bb-bestseller') === 'true';
        var show;
        if (isAll) {
          // bestsellers across ranges; if none are flagged, fall back to first N
          show = anyBest ? best : shown < max;
        } else {
          show = r === range; // complete set for the range
        }
        if (show) shown++;
        el.classList.toggle('is-hidden', !show);
      });
    }

    totalCount() {
      var self = this;
      return this.order.reduce(function (s, vid) { return s + (self.selection[vid] || 0); }, 0);
    }

    kitMeta(vid) {
      var card = this.querySelector(SEL.kit + '[data-variant-id="' + vid + '"]');
      return {
        title: card ? card.getAttribute('data-title') : '',
        image: card ? card.getAttribute('data-image') : '',
        price: card ? (parseInt(card.getAttribute('data-price'), 10) || 0) : 0
      };
    }

    subtotalCents() {
      var self = this;
      return this.order.reduce(function (s, vid) {
        return s + (self.selection[vid] || 0) * self.kitMeta(vid).price;
      }, 0);
    }

    /** highest tier whose min <= count; 0 if none */
    discountFor(count) {
      var pct = 0;
      for (var i = 0; i < this.ladder.length; i++) {
        if (count >= this.ladder[i].min) pct = this.ladder[i].pct;
      }
      return pct;
    }

    /** next tier above count, or null at the top */
    nextTier(count) {
      for (var i = 0; i < this.ladder.length; i++) {
        if (this.ladder[i].min > count) return this.ladder[i];
      }
      return null;
    }

    changeQty(vid, delta, silent) {
      var current = this.selection[vid] || 0;
      if (delta > 0) {
        if (!current) this.order.push(vid);
        this.selection[vid] = current + 1;
      } else if (delta < 0 && current > 0) {
        this.selection[vid] = current - 1;
        if (this.selection[vid] <= 0) {
          delete this.selection[vid];
          this.order = this.order.filter(function (v) { return v !== vid; });
        }
      }
      if ((this.selection[vid] || 0) !== current) {
        if (!this._analyticsStarted) { this._analyticsStarted = true; this.analyticsEvent('bundle_started'); }
        this.analyticsEvent('bundle_selection_changed', { variant_id: String(vid), action: delta > 0 ? 'add' : 'remove' });
      }
      if (!silent) this.render();
    }

    analyticsEvent(name, detail) {
      document.dispatchEvent(new CustomEvent('terra:bundle', { detail: Object.assign({
        event: name, kit_count: this.totalCount(), purchase_mode: this.mode
      }, detail || {}) }));
    }

    render() {
      var self = this;
      var count = this.totalCount();
      var pct = this.discountFor(count);
      var next = this.nextTier(count);

      var subtotal = this.subtotalCents();
      var total = Math.round(subtotal * (1 - pct / 100));
      var saved = subtotal - total;

      var showWas = pct > 0 && subtotal > 0;
      if (this.counterEl) this.counterEl.textContent = String(count);
      if (this.mCount) this.mCount.textContent = String(count);
      // Feed the free-gift reward ladder directly (it listens for this; the
      // MutationObserver fallback on the counter can miss/race the update).
      document.dispatchEvent(new CustomEvent('bundle:updated', { detail: { count: count } }));
      if (this.tierEl) {
        this.tierEl.textContent = pct > 0 ? pct + '% off' : (this.cfg.strings.noTier || 'No discount yet');
        this.tierEl.classList.toggle('is-on', pct > 0);
      }
      if (this.totalEl) this.totalEl.textContent = money(total, this.cfg);
      if (this.mTotal) this.mTotal.textContent = money(total, this.cfg);
      if (this.wasEl) {
        this.wasEl.textContent = showWas ? money(subtotal, this.cfg) : '';
        this.wasEl.hidden = !showWas;
      }
      if (this.mWas) {
        this.mWas.textContent = showWas ? money(subtotal, this.cfg) : '';
        this.mWas.hidden = !showWas;
      }
      // prominent "Total savings" — show only once a discount is active
      if (this.savingsBox) {
        if (this.savingsAmt) this.savingsAmt.textContent = money(saved, this.cfg);
        this.savingsBox.hidden = saved <= 0;
      }

      // progress bar toward next tier (or full at top)
      if (this.progressEl) {
        var pctFill;
        if (next) {
          var prevMin = pct > 0 ? this.currentTierMin(count) : 0;
          pctFill = Math.min(100, Math.round(((count - prevMin) / (next.min - prevMin)) * 100));
        } else { pctFill = 100; }
        this.progressEl.style.setProperty('--bb-fill', pctFill + '%');
      }

      // segmented tier meter — light up reached segments, highlight the current tier
      if (this.segs && this.segs.length) {
        var reachedIdx = -1;
        this.segs.forEach(function (seg, i) {
          var c = parseInt(seg.getAttribute('data-seg-count'), 10) || 0;
          if (count >= c) reachedIdx = i;
        });
        this.segs.forEach(function (seg, i) {
          seg.classList.toggle('is-reached', i <= reachedIdx);
          seg.classList.toggle('is-current', i === reachedIdx);
        });
      }

      // kit cards
      this.querySelectorAll(SEL.kit).forEach(function (card) {
        var vid = card.getAttribute('data-variant-id');
        var qty = self.selection[vid] || 0;
        var badge = card.querySelector(SEL.badge);
        var stepper = card.querySelector(SEL.stepper);
        var add = card.querySelector(SEL.add);
        var cnt = card.querySelector('[data-bb-count]');
        card.classList.toggle('is-selected', qty > 0);
        if (badge) { badge.textContent = qty; badge.hidden = qty === 0; }
        if (cnt) cnt.textContent = qty;
        if (stepper) stepper.hidden = qty === 0;
        if (add) add.hidden = qty > 0;
      });

      // chips tray (dynamic — one filled chip per kit, dashed placeholders up to the minimum)
      if (this.slotsEl) {
        var html = '';
        var prevQty = this._prevSlotQty || {};
        this.order.forEach(function (vid) {
          var qty = self.selection[vid] || 0;
          if (qty <= 0) return;
          var meta = self.kitMeta(vid);
          var initial = (meta.title || '?').charAt(0);
          // animate ONLY the slot that just changed (not the whole tray)
          var slotCls = 'bb-slot bb-slot--filled';
          if (prevQty[vid] === undefined) slotCls += ' is-new';
          else if (qty > prevQty[vid]) slotCls += ' is-bumped';
          // one slot per UNIQUE kit, with a ×N badge when more than one
          html += '<button type="button" class="' + slotCls + '" data-bb-remove="' + vid +
            '" title="' + (meta.title || '') + (qty > 1 ? ' ×' + qty : '') +
            '" aria-label="Remove one ' + (meta.title || 'item') + '">' +
            (meta.image
              ? '<img src="' + meta.image + '" alt="" decoding="async">'
              : '<span class="bb-slot__ph" aria-hidden="true">' + initial + '</span>') +
            (qty > 1 ? '<span class="bb-slot__qty" aria-hidden="true">×' + qty + '</span>' : '') +
            '<span class="bb-slot__x" aria-hidden="true">×</span></button>';
        });
        var emptyNeeded = Math.max(0, this.minKits - count);
        for (var e = 0; e < emptyNeeded; e++) {
          html += '<span class="bb-slot bb-slot--empty' + (e === 0 ? ' bb-slot--next' : '') + '" aria-hidden="true"></span>';
        }
        // while below the minimum, lay the boxes in a row with a little note to their right
        var starting = count < this.minKits;
        this.slotsEl.classList.toggle('is-starting', starting);
        if (starting) {
          html += '<span class="bb-slots__min"><span class="bb-slots__min-dot" aria-hidden="true"></span>Pick at least ' + this.minKits + ' to start</span>';
        }
        this.slotsEl.innerHTML = html;
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.slotsEl.querySelectorAll('[data-bb-remove]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var vid = btn.getAttribute('data-bb-remove');
            var qty = self.selection[vid] || 0;
            // more than one of this kit, or reduced motion: just decrement (re-render updates the badge)
            if (qty > 1 || reduceMotion) { self.changeQty(vid, -1); return; }
            // last one: play the exit animation, then remove
            if (btn.classList.contains('is-leaving')) return;
            btn.classList.add('is-leaving');
            var done = false;
            var finish = function () { if (done) return; done = true; self.changeQty(vid, -1); };
            btn.addEventListener('animationend', finish, { once: true });
            setTimeout(finish, 260); // fallback if animationend doesn't fire
          });
        });
        // remember this render's quantities so the next render animates only what changed
        var snap = {};
        this.order.forEach(function (vid) { var q = self.selection[vid] || 0; if (q > 0) snap[vid] = q; });
        this._prevSlotQty = snap;
      }

      // hint / nudge (shared by the panel + the mobile bar)
      var msg = '';
      if (count < this.minKits) {
        msg = tmpl(this.cfg.strings.min, { n: this.minKits - count, min: this.minKits });
      } else if (next) {
        msg = tmpl(this.cfg.strings.next, { n: next.min - count, pct: next.pct + '%' });
      } else {
        msg = this.cfg.strings.max || 'Maximum savings unlocked!';
      }
      if (this.hintEl) this.hintEl.textContent = msg;
      if (this.mHint) this.mHint.textContent = msg;

      var ready = count >= this.minKits;
      if (this.submitBtn) this.submitBtn.disabled = !ready;
      if (this.mSubmit) this.mSubmit.disabled = !ready;
      this.classList.toggle('bb--ready', ready);
    }

    currentTierMin(count) {
      var min = 0;
      for (var i = 0; i < this.ladder.length; i++) {
        if (count >= this.ladder[i].min) min = this.ladder[i].min;
      }
      return min;
    }

    buildItems() {
      var self = this;
      var planId = (this.mode === 'subscribe' && this.sellingPlanSelect && this.sellingPlanSelect.value) || '';
      var html = '';
      var i = 0;
      this.order.forEach(function (vid) {
        var qty = self.selection[vid];
        function input(name, val) {
          html += '<input type="hidden" name="items[' + i + '][' + name + ']" value="' + String(val).replace(/"/g, '&quot;') + '">';
        }
        input('id', vid);
        input('quantity', qty);
        input('properties][_byob', '1');
        if (planId) html += '<input type="hidden" name="items[' + i + '][selling_plan]" value="' + planId + '">';
        i++;
      });
      this.itemsEl.innerHTML = html;
    }

    addToCart() {
      if (this.submitBtn.disabled) return;
      var self = this;
      // Don't silently duplicate a box: if the cart already has BYOB kits, ask the
      // shopper whether to add these as well or hot-swap (replace) the existing box.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var existing = (cart.items || []).filter(function (it) {
            return it.properties && String(it.properties._byob) === '1';
          });
          if (!existing.length) { self.commitAdd(); return; }
          self.openBoxConflict(existing, function (mode) {
            if (mode === 'replace') self.replaceBox(existing);
            else self.commitAdd();
          });
        })
        .catch(function () { self.commitAdd(); });
    }

    commitAdd() {
      this.buildItems();
      // Submission is an intent event. Only Shopify's pixel records a successful cart addition.
      this.analyticsEvent('bundle_submitted');
      var drawerEnabled = window.theme && window.theme.settings && window.theme.settings.cartDrawerEnabled;
      if (drawerEnabled) {
        document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { button: this.submitBtn }, bubbles: true }));
      } else {
        this.directAdd();
      }
    }

    // Hot-swap: zero out the existing BYOB lines, then add the freshly-built box.
    replaceBox(existing) {
      var self = this;
      var updates = {};
      existing.forEach(function (it) { updates[it.key] = 0; });
      this.submitBtn.classList.add('is-loading');
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function () { self.submitBtn.classList.remove('is-loading'); self.commitAdd(); })
        .catch(function () { self.submitBtn.classList.remove('is-loading'); self.commitAdd(); });
    }

    boxKitCount() {
      var n = 0, self = this;
      this.order.forEach(function (vid) { n += self.selection[vid] || 0; });
      return n;
    }

    openBoxConflict(existing, cb) {
      var existingCount = existing.reduce(function (s, it) { return s + (it.quantity || 0); }, 0);
      var newCount = this.boxKitCount();
      var wrap = document.createElement('div');
      wrap.className = 'bb-modal';
      wrap.setAttribute('role', 'dialog');
      wrap.setAttribute('aria-modal', 'true');
      wrap.setAttribute('aria-label', 'You already have a box in your bag');
      wrap.innerHTML =
        '<div class="bb-modal__backdrop" data-bb-modal-close></div>' +
        '<div class="bb-modal__card">' +
          '<button type="button" class="bb-modal__x" aria-label="Close" data-bb-modal-close>&times;</button>' +
          '<h3 class="bb-modal__title">You already have a box in your bag</h3>' +
          '<p class="bb-modal__body">Your bag already has <strong>' + existingCount + ' ' + (existingCount === 1 ? 'kit' : 'kits') + '</strong>. ' +
            'Add these <strong>' + newCount + ' more</strong>, or replace your box with this one?</p>' +
          '<div class="bb-modal__actions">' +
            '<button type="button" class="bb-modal__btn bb-modal__btn--primary" data-bb-modal-add>Add to my box</button>' +
            '<button type="button" class="bb-modal__btn bb-modal__btn--ghost" data-bb-modal-replace>Replace my box</button>' +
          '</div>' +
        '</div>';
      document.body.appendChild(wrap);
      var html = document.documentElement;
      var prevOverflow = html.style.overflow;
      html.style.overflow = 'hidden';
      function close() {
        html.style.overflow = prevOverflow;
        document.removeEventListener('keydown', onKey);
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      }
      function onKey(e) { if (e.key === 'Escape') close(); }
      document.addEventListener('keydown', onKey);
      wrap.addEventListener('click', function (e) {
        var t = e.target;
        if (t.hasAttribute('data-bb-modal-close')) close();
        else if (t.hasAttribute('data-bb-modal-add')) { close(); cb('add'); }
        else if (t.hasAttribute('data-bb-modal-replace')) { close(); cb('replace'); }
      });
      var primary = wrap.querySelector('[data-bb-modal-add]');
      if (primary) primary.focus();
    }

    directAdd() {
      var self = this;
      var url = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
      var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('is-loading');
      fetch(url + '.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: new FormData(this.form)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) {
            self.analyticsEvent('bundle_error', { error_code: 'cart_rejected' });
            self.submitBtn.classList.remove('is-loading');
            self.submitBtn.disabled = false;
            if (self.hintEl) self.hintEl.textContent = res.description || res.message || 'Could not add to cart.';
            return;
          }
          window.location = cartUrl;
        })
        .catch(function () {
          self.analyticsEvent('bundle_error', { error_code: 'network_error' });
          self.submitBtn.classList.remove('is-loading');
          self.submitBtn.disabled = false;
        });
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
